// Platform Admin — Phase 2 money panels (ADMIN_UPGRADE_PLAN 20–26).
// RevenuePanel: MRR/ARR cards + past-due (dunning) queue.
// InvoicesPanel: cross-tenant invoice browser with manual mark-paid.
// CouponsPanel:  discount-code CRUD.
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import {
  revenueSummary, listInvoices, markInvoicePaid,
  listCoupons, saveCoupon, deleteCoupon,
  type RevenueSummary, type AdminInvoice, type Coupon, type CouponInput,
} from '../../lib/padminMoney';
import { useConfirm } from '../ui/ConfirmDialog';

const CARD ='rounded-card border border-slate-200 bg-white p-5 shadow-sm';
const FLD = 'w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary';
const BTN = 'rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40';
const GHOST = 'rounded-[8px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-ink-2 hover:border-primary hover:text-primary';

function fmtDate(ms: number | null | undefined): string {
  return ms ? new Date(ms).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
}
// Amounts are OMR despite the `...Usd` field names (see lib/billing.ts money()).
const usd = (n: number) => `${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })} OMR`;

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'warn' }) {
  return (
    <div className={CARD}>
      <span className="block text-[11px] font-bold uppercase tracking-wide text-ink-3">{label}</span>
      <b className={cn('mt-1 block text-2xl font-extrabold',
        tone === 'good' ? 'text-primary' : tone === 'warn' ? 'text-amber-600' : 'text-ink')}>{value}</b>
    </div>
  );
}

function ErrBox({ msg }: { msg: string | null }) {
  if (!msg) return null;
  return <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{msg}</div>;
}

/** Promise-based single-line text prompt (replaces native window.prompt). */
interface TextPromptReq {
  title: string;
  minLength: number;
  resolve: (value: string | null) => void;
}

function TextPromptModal({ req, onDone }: { req: TextPromptReq; onDone: (value: string | null) => void }) {
  const { t } = useI18n();
  const [value, setValue] = useState('');
  const ok = value.trim().length >= req.minLength;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="presentation">
      <button type="button" aria-label={t('common.cancel')} onClick={() => onDone(null)} className="absolute inset-0 bg-slate-900/40" />
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="pa-money-prompt-label"
        onSubmit={(e) => { e.preventDefault(); if (ok) onDone(value.trim()); }}
        className={cn(CARD, 'relative z-10 w-full max-w-sm shadow-lg')}
      >
        <label id="pa-money-prompt-label" htmlFor="pa-money-prompt" className="block text-sm font-bold text-ink">{req.title}</label>
        <input id="pa-money-prompt" autoFocus value={value} onChange={(e) => setValue(e.target.value)} className={cn(FLD, 'mt-3')} />
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={() => onDone(null)} className={GHOST}>{t('common.cancel')}</button>
          <button type="submit" disabled={!ok} className={BTN}>{t('common.confirm')}</button>
        </div>
      </form>
    </div>
  );
}

// ── Revenue + dunning ────────────────────────────────────────────────────────
export function RevenuePanel({ onOpenTenant }: { onOpenTenant?: (id: string) => void }) {
  const { t } = useI18n();
  const [data, setData] = useState<RevenueSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void revenueSummary().then(setData).catch((e: Error) => setErr(e.message));
  }, []);

  if (err) return <ErrBox msg={err} />;
  if (!data) return <div className="py-10 text-center text-sm text-ink-3">…</div>;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label={t('pa.rev.mrr')} value={usd(data.mrrUsd)} tone="good" />
        <Stat label={t('pa.rev.arr')} value={usd(data.arrUsd)} />
        <Stat label={t('pa.rev.paying')} value={`${data.paying}/${data.tenants}`} />
        <Stat label={t('pa.rev.trials')} value={String(data.trials)} />
        <Stat label={t('pa.rev.conversion')} value={`${data.conversionPct}%`} />
      </div>

      <div className={CARD}>
        <b className="block text-sm text-ink">{t('pa.rev.dunning')}</b>
        {data.pastDue.length === 0 ? (
          <p className="mt-2 text-xs text-ink-3">{t('pa.rev.dunningEmpty')}</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-200">
            {data.pastDue.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 py-2.5">
                <button
                  type="button"
                  onClick={() => onOpenTenant?.(r.id)}
                  className="text-[13px] font-bold text-ink hover:text-primary hover:underline"
                >
                  {r.name}
                </button>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-ink-2">{r.planId}</span>
                <span className="text-[11px] text-ink-3">{t('pa.rev.renewal')}: {fmtDate(r.renewalAt)}</span>
                <span className="ms-auto rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold text-amber-700">
                  {r.daysLate} {t('pa.rev.daysLate')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Invoices ─────────────────────────────────────────────────────────────────
const INV_PILL: Record<string, string> = {
  paid: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
};

export function InvoicesPanel() {
  const { t } = useI18n();
  const [rows, setRows] = useState<AdminInvoice[] | null>(null);
  const [filter, setFilter] = useState<'' | 'pending' | 'paid' | 'failed'>('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [textReq, setTextReq] = useState<TextPromptReq | null>(null);
  const askText = (title: string, minLength = 1) =>
    new Promise<string | null>((resolve) => setTextReq({ title, minLength, resolve }));

  const load = useCallback(() => {
    setRows(null);
    listInvoices(filter || undefined, 200).then(setRows).catch((e: Error) => setErr(e.message));
  }, [filter]);
  useEffect(load, [load]);

  async function pay(inv: AdminInvoice) {
    const reason = await askText(t('pa.inv.markPaidPrompt'), 3);
    if (!reason) return;
    setBusy(inv.id); setErr(null);
    try {
      await markInvoicePaid({ tenantId: inv.tenantId, invoiceId: inv.id, reason });
      load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }

  const TABS: { k: typeof filter; label: string }[] = [
    { k: '', label: t('pa.inv.filter.all') },
    { k: 'pending', label: t('pa.inv.status.pending') },
    { k: 'paid', label: t('pa.inv.status.paid') },
    { k: 'failed', label: t('pa.inv.status.failed') },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-sm text-ink">{t('pa.inv.title')}</b>
        <div className="ms-auto flex gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.k || 'all'}
              type="button"
              onClick={() => setFilter(tab.k)}
              className={cn('rounded-full px-3 py-1 text-xs font-bold',
                filter === tab.k ? 'bg-ink text-white' : 'bg-white text-ink-3 border border-slate-200 hover:text-ink')}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <ErrBox msg={err} />

      <div className="rounded-card border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr>
                {['pa.inv.col.tenant', 'pa.inv.col.plan', 'pa.inv.col.amount', 'pa.inv.col.status', 'pa.inv.col.created', 'pa.inv.col.ref', 'pa.t.col.actions'].map((k) => (
                  <th key={k} className="whitespace-nowrap bg-surface px-3.5 py-3 text-start text-[11px] font-bold uppercase tracking-wide text-ink-3">
                    {t(k)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows === null && (
                <tr><td colSpan={7} className="px-3.5 py-8 text-center text-sm text-ink-3">…</td></tr>
              )}
              {rows?.length === 0 && (
                <tr><td colSpan={7} className="px-3.5 py-8 text-center text-sm text-ink-3">{t('pa.inv.empty')}</td></tr>
              )}
              {rows?.map((inv) => (
                <tr key={`${inv.tenantId}/${inv.id}`} className="border-t border-slate-200">
                  <td className="whitespace-nowrap px-3.5 py-3 text-[13px] font-semibold text-ink">{inv.tenantName}</td>
                  <td className="whitespace-nowrap px-3.5 py-3 text-[13px] text-ink-2">{inv.planId} · {inv.cycle}</td>
                  <td className="whitespace-nowrap px-3.5 py-3 text-[13px] font-bold text-ink">{usd(inv.amountUsd)}</td>
                  <td className="px-3.5 py-3">
                    <span className={cn('rounded-full px-2.5 py-0.5 text-[11px] font-bold', INV_PILL[inv.status] ?? 'bg-slate-100 text-ink-2')}>
                      {t(`pa.inv.status.${inv.status}`)}
                    </span>
                    {inv.manual && <span className="ms-1 text-[10px] font-bold text-ink-3">({t('pa.inv.manual')})</span>}
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-3 text-xs text-ink-3">{fmtDate(inv.createdAt)}</td>
                  <td className="max-w-[160px] truncate px-3.5 py-3 font-mono text-[11px] text-ink-3">{inv.providerRef ?? '—'}</td>
                  <td className="whitespace-nowrap px-3.5 py-3">
                    {inv.status !== 'paid' && (
                      <button type="button" onClick={() => void pay(inv)} disabled={busy === inv.id} className={GHOST}>
                        {busy === inv.id ? '…' : t('pa.inv.markPaid')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {textReq && (
        <TextPromptModal req={textReq} onDone={(v) => { setTextReq(null); textReq.resolve(v); }} />
      )}
    </div>
  );
}

// ── Coupons ──────────────────────────────────────────────────────────────────
const BLANK: CouponInput = { code: '', type: 'percent', value: 10, active: true };

export function CouponsPanel() {
  const { t } = useI18n();
  const [rows, setRows] = useState<Coupon[] | null>(null);
  const [draft, setDraft] = useState<CouponInput | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();

  const load = useCallback(() => {
    listCoupons().then(setRows).catch((e: Error) => setErr(e.message));
  }, []);
  useEffect(load, [load]);

  async function save() {
    if (!draft) return;
    setBusy(true); setErr(null);
    try {
      await saveCoupon({ ...draft, code: draft.code.trim().toUpperCase() });
      setDraft(null); load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  async function remove(code: string) {
    if (busy) return; // a double-click would send two deletes; the second 404s
    if (!(await confirm({ title: t('pa.cp.confirmDelete'), body: `${code} — ${t('common.cannotUndo')}`, danger: true }))) return;
    setBusy(true); setErr(null);
    try { await deleteCoupon(code); load(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <b className="block text-sm text-ink">{t('pa.cp.title')}</b>
          <p className="text-xs text-ink-3">{t('pa.cp.note')}</p>
        </div>
        <button type="button" onClick={() => setDraft({ ...BLANK })} className={cn(BTN, 'ms-auto')}>
          + {t('pa.cp.add')}
        </button>
      </div>
      <ErrBox msg={err} />

      {draft && (
        <div className={cn(CARD, 'space-y-3')}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block">
              <span className="mb-1 block text-xs font-bold text-ink-2">{t('pa.cp.code')}</span>
              <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
                placeholder="WELCOME20" aria-label={t('pa.cp.code')} className={cn(FLD, 'font-mono uppercase')} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-bold text-ink-2">{t('pa.cp.type')}</span>
              <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value as 'percent' | 'fixed' })} className={FLD}>
                <option value="percent">{t('pa.cp.percent')}</option>
                <option value="fixed">{t('pa.cp.fixed')}</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-bold text-ink-2">{t('pa.cp.value')}</span>
              <input type="number" min={1} value={draft.value}
                onChange={(e) => setDraft({ ...draft, value: Number(e.target.value) })} aria-label={t('pa.cp.value')} className={FLD} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-bold text-ink-2">{t('pa.cp.limit')}</span>
              <input type="number" min={1} value={draft.maxRedemptions ?? ''}
                onChange={(e) => setDraft({ ...draft, maxRedemptions: e.target.value ? Number(e.target.value) : undefined })}
                placeholder={t('pa.cp.unlimited')} aria-label={t('pa.cp.limit')} className={FLD} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-bold text-ink-2">{t('pa.cp.expires')}</span>
              <input type="date"
                value={draft.expiresAt ? new Date(draft.expiresAt).toISOString().slice(0, 10) : ''}
                // Expire at the END of the chosen day, not the first millisecond of it.
                onChange={(e) => setDraft({
                  ...draft,
                  expiresAt: e.target.value ? new Date(`${e.target.value}T23:59:59Z`).getTime() : undefined,
                })}
                className={FLD} />
            </label>
            <label className="flex items-center gap-2 self-end pb-2 text-sm font-semibold text-ink-2">
              <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
              {t('pa.cp.active')}
            </label>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => void save()} disabled={busy || draft.code.trim().length < 3} className={BTN}>
              {busy ? '…' : t('pa.cp.save')}
            </button>
            <button type="button" onClick={() => setDraft(null)} className={GHOST}>✕</button>
          </div>
        </div>
      )}

      <div className="rounded-card border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr>
                {['pa.cp.code', 'pa.cp.type', 'pa.cp.value', 'pa.cp.used', 'pa.cp.expires', 'pa.t.col.actions'].map((k) => (
                  <th key={k} className="whitespace-nowrap bg-surface px-3.5 py-3 text-start text-[11px] font-bold uppercase tracking-wide text-ink-3">
                    {t(k)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows === null && <tr><td colSpan={6} className="px-3.5 py-8 text-center text-sm text-ink-3">…</td></tr>}
              {rows?.length === 0 && <tr><td colSpan={6} className="px-3.5 py-8 text-center text-sm text-ink-3">{t('pa.cp.empty')}</td></tr>}
              {rows?.map((c) => (
                <tr key={c.code} className="border-t border-slate-200">
                  <td className="whitespace-nowrap px-3.5 py-3 font-mono text-[13px] font-bold text-ink">
                    {c.code}
                    {!c.active && <span className="ms-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-ink-3">off</span>}
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-3 text-[13px] text-ink-2">
                    {c.type === 'percent' ? t('pa.cp.percent') : t('pa.cp.fixed')}
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-3 text-[13px] font-bold text-ink">
                    {c.type === 'percent' ? `${c.value}%` : usd(c.value)}
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-3 text-[13px] text-ink-2">
                    {c.redemptions}{c.maxRedemptions ? ` / ${c.maxRedemptions}` : ''}
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-3 text-xs text-ink-3">
                    {c.expiresAt ? fmtDate(c.expiresAt) : t('pa.cp.never')}
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-3">
                    <div className="flex gap-1.5">
                      <button type="button" className={GHOST} onClick={() => setDraft({
                        code: c.code, type: c.type, value: c.value, planIds: c.planIds,
                        expiresAt: c.expiresAt ?? undefined, maxRedemptions: c.maxRedemptions ?? undefined,
                        active: c.active,
                      })}>
                        {t('pa.plans.edit')}
                      </button>
                      <button type="button" className={cn(GHOST, 'hover:border-red-300 hover:text-red-600')} onClick={() => void remove(c.code)}>
                        {t('pa.cp.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
